import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GetSiteGQL, GetSiteRedirectsGQL, UpdateSiteRedirectsGQL, UrlRedirectInput } from '../../graphql/graphql';
import { ToastService } from '../../shared/toast/toast.service';

const FROM_REGEX = /^\/[^\s?#]+$/;
const TO_REGEX = /^(\/[^\s]*|https?:\/\/[^\s/][^\s]*)$/;

interface RedirectRow {
  from: string;
  to: string;
  createdAt?: string;
  editing: boolean;
  draft: { from: string; to: string };
  errors: { from?: string; to?: string };
}

@Component({
  selector: 'app-redirects',
  templateUrl: './redirects.component.html',
  styleUrls: ['./redirects.component.scss'],
  standalone: false
})
export class RedirectsComponent implements OnInit {
  siteUrl = '';
  siteId = '';
  rows: RedirectRow[] = [];
  saving = false;

  showDeleteModal = false;
  deleteIndex: number | null = null;

  constructor(
    private route: ActivatedRoute,
    private getSiteGQL: GetSiteGQL,
    private getSiteRedirectsGQL: GetSiteRedirectsGQL,
    private updateSiteRedirectsGQL: UpdateSiteRedirectsGQL,
    private toast: ToastService
  ) {}

  ngOnInit() {
    this.siteUrl = this.route.parent?.snapshot.params['url'] || '';
    if (!this.siteUrl) return;
    this.getSiteGQL.watch({ variables: { url: this.siteUrl, categoryId: '' } }).valueChanges.subscribe(result => {
      if (result.data?.site) {
        this.siteId = result.data.site._id!;
        this.loadRedirects();
      }
    });
  }

  loadRedirects() {
    this.getSiteRedirectsGQL
      .watch({ variables: { url: this.siteUrl, categoryId: '' }, fetchPolicy: 'network-only' })
      .valueChanges.subscribe(result => {
        const redirects = (result.data?.site?.redirects || []) as Array<{ from: string; to: string; createdAt: string }>;
        this.rows = redirects.map(r => ({
          from: r.from,
          to: r.to,
          createdAt: r.createdAt,
          editing: false,
          draft: { from: r.from, to: r.to },
          errors: {}
        }));
      });
  }

  addRow() {
    this.rows.unshift({
      from: '',
      to: '',
      editing: true,
      draft: { from: '', to: '' },
      errors: {}
    });
  }

  edit(i: number) {
    const row = this.rows[i];
    row.draft = { from: row.from, to: row.to };
    row.errors = {};
    row.editing = true;
  }

  cancel(i: number) {
    const row = this.rows[i];
    if (!row.createdAt) {
      this.rows.splice(i, 1);
      return;
    }
    row.draft = { from: row.from, to: row.to };
    row.errors = {};
    row.editing = false;
  }

  save(i: number) {
    const row = this.rows[i];
    if (!this.validateDraft(row, i)) return;

    const previous = { from: row.from, to: row.to, createdAt: row.createdAt };
    row.from = row.draft.from;
    row.to = row.draft.to;
    row.editing = false;
    row.errors = {};

    this.persistAll({
      onError: () => {
        row.from = previous.from;
        row.to = previous.to;
        row.createdAt = previous.createdAt;
        row.draft = { from: previous.from, to: previous.to };
        row.editing = true;
      }
    });
  }

  askDelete(i: number) {
    this.deleteIndex = i;
    this.showDeleteModal = true;
  }

  cancelDelete() {
    this.showDeleteModal = false;
    this.deleteIndex = null;
  }

  confirmDelete() {
    if (this.deleteIndex === null) return;
    const i = this.deleteIndex;
    this.showDeleteModal = false;
    this.deleteIndex = null;

    const removed = this.rows.splice(i, 1)[0];
    if (!removed.createdAt) return;

    this.persistAll({
      onError: () => {
        this.rows.splice(i, 0, removed);
      }
    });
  }

  private validateDraft(row: RedirectRow, i: number): boolean {
    const errors: { from?: string; to?: string } = {};
    const from = row.draft.from.trim();
    const to = row.draft.to.trim();

    if (!FROM_REGEX.test(from)) {
      errors.from = 'Must be a relative path starting with / (e.g. /old-page)';
    } else if (this.rows.some((r, idx) => idx !== i && (r.editing ? r.draft.from : r.from) === from)) {
      errors.from = 'A redirect with this "from" path already exists';
    }

    if (!TO_REGEX.test(to)) {
      errors.to = 'Must be a path (/...) or absolute URL (http(s)://...)';
    }

    row.draft.from = from;
    row.draft.to = to;
    row.errors = errors;
    return !errors.from && !errors.to;
  }

  private persistAll(opts: { onError: () => void }) {
    if (!this.siteId) return;
    this.saving = true;

    const redirects: UrlRedirectInput[] = this.rows
      .filter(r => !r.editing && r.from && r.to)
      .map(r => ({
        from: r.from,
        to: r.to,
        createdAt: r.createdAt as any
      }));

    this.updateSiteRedirectsGQL.mutate({ variables: { siteId: this.siteId, redirects } }).subscribe({
      next: (result) => {
        this.saving = false;
        const saved = result.data?.updateSiteRedirects?.redirects || [];
        const byFrom = new Map(saved.map(r => [r.from, r.createdAt]));
        for (const row of this.rows) {
          const ts = byFrom.get(row.from);
          if (ts) row.createdAt = ts;
        }
        this.toast.success('Redirects saved');
      },
      error: (err) => {
        this.saving = false;
        console.error('Error saving redirects:', err);
        opts.onError();
        this.toast.error(err?.message || 'Error saving redirects');
      }
    });
  }
}
